import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  X, Truck, Package, Save, Loader,
  Radio, History, Pencil, Building2, Boxes,
  Link2, Unlink, MapPin, Filter, Map,
  AlertTriangle, Hexagon, Route, Navigation, ShieldCheck, BellRing,
  Contact, UserCheck, UserMinus
} from 'lucide-react'
import { apiClient } from '../config/api'
import SearchableSelect from './common/SearchableSelect'
import {
  ASSET_TYPE_OPTIONS, VEHICLE_ICON_OPTIONS, trackingPresentation, vehicleIconComponent
} from '../utils/vehiclePresentation'
import ControlCenterSummary from './vehicle-control-center/ControlCenterSummary'
import VehicleZonesTab from './vehicle-control-center/VehicleZonesTab'
import VehicleRulesTab from './vehicle-control-center/VehicleRulesTab'
import VehicleAlertsTab from './vehicle-control-center/VehicleAlertsTab'
import { vehicleControlCenterService } from '../services/vehicleControlCenterService'
import type {
  ControlCenterMission, ControlCenterRule, ControlCenterZone, VehicleControlCenter, ZoneType
} from '../services/vehicleControlCenterTypes'
import TransportRoutePicker, {
  TransportRouteSelection, applyTransportSelection, emptyRouteSelection, routeSelectionComplete
} from './TransportRoutePicker'

interface FleetVehicle {
  id: number
  vehicle_code: string
  license_plate: string
  make: string
  brand?: string
  model: string
  year: number
  vehicle_type: string
  vehicle_source: 'ASSET' | 'CONTAINER'
  customer_name?: string
  customer_code?: string
  custom_name?: string
  current_location?: string
  balise_codes?: string
  balise_count: number
  tracking_status?: 'LIVE' | 'STALE' | 'OFFLINE' | 'NOT_TRACKED'
  balise_last_seen?: string
  balise_battery?: number
  length?: number
  width?: number
  height?: number
  weight?: number
  max_weight?: number
  engine_type?: string
  consumption?: number
  max_speed?: number
  customer_id?: number
  asset_type?: string
  icon_key?: string
  equipment_reference?: string
  container_type?: string
  container_owner?: string
  container_origin?: string
  container_destination?: string
  booking_reference?: string
  operational_identifier?: string
  current_speed?: number
}

export type VehicleModalTab = 'history' | 'details' | 'assignment' | 'driver' | 'zones' | 'rules' | 'alerts'

interface VehicleDetailsModalProps {
  isOpen: boolean
  vehicle: FleetVehicle
  activeTab: VehicleModalTab
  onTabChange: (tab: VehicleModalTab) => void
  /** When true the Details tab opens directly in edit mode (Edit button in the list). */
  startEditing?: boolean
  embedded?: boolean
  onClose: () => void
  onSuccess: () => void
  onShowOnMap?: (vehicle: FleetVehicle) => void
  onCreateZone?: (type: ZoneType, vehicle: FleetVehicle) => boolean | void
  onManageZone?: (zone: ControlCenterZone, type: ZoneType, vehicle: FleetVehicle) => void
  onManageMission?: (mission: ControlCenterMission, vehicle: FleetVehicle) => void
  onCreateAlertRule?: (vehicle: FleetVehicle) => void
  onEditAlertRule?: (rule: ControlCenterRule, vehicle: FleetVehicle) => void
  showMessage?: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void
}

type ActivityCategory = 'TRACKING' | 'ALERT' | 'GEOFENCE' | 'ASSIGNMENT' | 'MISSION'

interface ActivityItem {
  id: string
  category: ActivityCategory
  timestamp: string
  title: string
  subtitle?: string | null
  severity?: string | null
  event_type?: string
  lock_code?: string
  latitude?: number
  longitude?: number
  speed?: number
  battery_level?: number
}

interface CustomerOption {
  id: number
  customer_code: string
  company_name: string
}

interface FleetOption {
  id: number
  name: string
  customer_name?: string
  member_count: number
}

interface DriverAssignment {
  id: number
  staffId: number
  driverName: string | null
  driverCode: string | null
  assignedFrom: string
  assignedTo: string | null
  isCurrent: boolean
  notes: string | null
  createdBy: string | null
}

interface DriverRecord {
  id: number
  name: string
  staffCode?: string | null
  licenceClass?: string | null
  currentAssignment?: { vehicleLabel?: string | null } | null
}

const inputCls = 'w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:outline-none'

const VehicleDetailsModal: React.FC<VehicleDetailsModalProps> = ({
  isOpen,
  vehicle,
  activeTab,
  onTabChange,
  startEditing = false,
  embedded = false,
  onClose,
  onSuccess,
  onShowOnMap,
  onCreateZone,
  onManageZone,
  onManageMission,
  onCreateAlertRule,
  onEditAlertRule,
  showMessage
}) => {
  const { t } = useTranslation()
  const [assignmentHistory, setAssignmentHistory] = useState<any[]>([])
  const [driverAssignments, setDriverAssignments] = useState<DriverAssignment[]>([])
  const [driverOptions, setDriverOptions] = useState<DriverRecord[]>([])
  const [driverLoading, setDriverLoading] = useState(false)
  const [driverBusy, setDriverBusy] = useState(false)
  const [selectedDriverId, setSelectedDriverId] = useState('')
  const [controlCenter, setControlCenter] = useState<VehicleControlCenter | null>(null)
  const [controlLoading, setControlLoading] = useState(false)
  const [controlError, setControlError] = useState<string | null>(null)

  // ---- Details (edit) state ----
  const [isEditing, setIsEditing] = useState(startEditing)
  const [saving, setSaving] = useState(false)
  // Transport route: empty selection = leave the existing assignment untouched
  const [route, setRoute] = useState<TransportRouteSelection>(emptyRouteSelection)
  const [routeTouched, setRouteTouched] = useState(false)
  const [editData, setEditData] = useState<Partial<FleetVehicle>>({
    custom_name: vehicle.custom_name || '',
    license_plate: vehicle.license_plate === 'N/A' ? '' : (vehicle.license_plate || ''),
    make: vehicle.make === 'Unknown' ? '' : (vehicle.make || ''),
    model: vehicle.model === 'Unknown' ? '' : (vehicle.model || ''),
    year: vehicle.year,
    current_location: vehicle.current_location || '',
    length: vehicle.length, width: vehicle.width, height: vehicle.height,
    weight: vehicle.weight, max_weight: vehicle.max_weight,
    engine_type: vehicle.engine_type || '', consumption: vehicle.consumption, max_speed: vehicle.max_speed,
    asset_type: vehicle.asset_type || vehicle.vehicle_type,
    icon_key: vehicle.icon_key,
    equipment_reference: vehicle.equipment_reference || vehicle.vehicle_code,
    container_type: vehicle.container_type,
    container_owner: vehicle.container_owner,
    container_origin: vehicle.container_origin,
    container_destination: vehicle.container_destination,
    booking_reference: vehicle.booking_reference
  })

  // ---- History (activity) tab state ----
  const ALL_CATEGORIES: ActivityCategory[] = ['TRACKING', 'ALERT', 'GEOFENCE', 'ASSIGNMENT', 'MISSION']
  const [histItems, setHistItems] = useState<ActivityItem[]>([])
  const [histTypes, setHistTypes] = useState<string[]>([])
  const [histCounts, setHistCounts] = useState<Record<string, number>>({})
  const [histTotal, setHistTotal] = useState(0)
  const [histLoading, setHistLoading] = useState(false)
  const [histFrom, setHistFrom] = useState('')
  const [histTo, setHistTo] = useState('')
  const [histType, setHistType] = useState('all')
  const [histCategories, setHistCategories] = useState<ActivityCategory[]>(ALL_CATEGORIES)

  // ---- Assignment tab state ----
  const [customers, setCustomers] = useState<CustomerOption[]>([])
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('')
  const [reassignmentReason, setReassignmentReason] = useState('')
  const [customerHistory, setCustomerHistory] = useState<any[]>([])
  const [customerBusy, setCustomerBusy] = useState(false)
  const [fleets, setFleets] = useState<FleetOption[]>([])
  const [vehicleFleets, setVehicleFleets] = useState<{ fleet_id: number, fleet_name: string }[]>([])
  const [selectedFleetId, setSelectedFleetId] = useState<string>('')
  const [fleetBusy, setFleetBusy] = useState(false)

  const loadControlCenter = useCallback(async () => {
    setControlLoading(true)
    setControlError(null)
    try {
      setControlCenter(await vehicleControlCenterService.get(vehicle.vehicle_source, vehicle.id))
    } catch (error: any) {
      console.error('Error loading vehicle control center:', error)
      setControlError(error?.response?.data?.error || t('vcc_load_failed', { defaultValue: 'Unable to load the vehicle control center.' }))
    } finally {
      setControlLoading(false)
    }
  }, [t, vehicle.id, vehicle.vehicle_source])

  useEffect(() => {
    if (isOpen) loadControlCenter()
  }, [isOpen, loadControlCenter])

  const dispatchControlAction = (action: string, detail: Record<string, unknown> = {}) => {
    window.dispatchEvent(new CustomEvent('fleet:vehicle-control-action', {
      detail: { action, vehicle, ...detail }
    }))
  }

  const handleCreateZone = (type: ZoneType) => {
    const accepted = onCreateZone
      ? onCreateZone(type, vehicle)
      : (dispatchControlAction('create-zone', { zoneType: type }), true)
    if (accepted !== false) onClose()
  }

  const handleCreateRule = () => {
    if (onCreateAlertRule) onCreateAlertRule(vehicle)
    else dispatchControlAction('create-rule')
    onClose()
  }

  const handleManageZone = (
    zone: ControlCenterZone,
    type: ZoneType
  ) => {
    if (onManageZone) onManageZone(zone, type, vehicle)
    else dispatchControlAction('manage-zone', { zone, zoneType: type })
    onClose()
  }

  const handleManageMission = (mission: ControlCenterMission) => {
    if (onManageMission) onManageMission(mission, vehicle)
    else dispatchControlAction('manage-mission', { mission })
    onClose()
  }

  const handleEditRule = (rule: ControlCenterRule) => {
    if (onEditAlertRule) onEditAlertRule(rule, vehicle)
    else dispatchControlAction('edit-rule', { rule })
    onClose()
  }

  useEffect(() => {
    if (!isOpen) return
    setRoute(emptyRouteSelection())
    setRouteTouched(false)
    setSelectedCustomerId(vehicle.customer_id != null ? String(vehicle.customer_id) : '')
    apiClient.get('/customers')
      .then(response => setCustomers(Array.isArray(response.data) ? response.data : []))
      .catch(() => setCustomers([]))
  }, [isOpen, vehicle.customer_id])

  // Load the unified activity feed when the History tab opens or filters change
  const loadActivity = async () => {
    try {
      setHistLoading(true)
      const params = new URLSearchParams()
      if (histFrom) params.set('from', new Date(histFrom).toISOString())
      if (histTo) params.set('to', new Date(`${histTo}T23:59:59`).toISOString())
      if (histType !== 'all') params.set('event_type', histType)
      if (histCategories.length < ALL_CATEGORIES.length) params.set('categories', histCategories.join(','))
      const res = await apiClient.get(`/vehicles/${vehicle.vehicle_source}/${vehicle.id}/history?${params.toString()}`)
      setHistItems(res.data?.events || [])
      setHistTypes(res.data?.event_types || [])
      setHistCounts(res.data?.counts || {})
      setHistTotal(res.data?.total || 0)
    } catch (error) {
      console.error('Error loading vehicle activity:', error)
      setHistItems([])
    } finally {
      setHistLoading(false)
    }
  }
  useEffect(() => {
    if (isOpen && activeTab === 'history') loadActivity()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeTab, histFrom, histTo, histType, histCategories, vehicle.id])

  const toggleCategory = (cat: ActivityCategory) => {
    // Category chips act as positive filters: clicking Alerts means "show
    // alerts", not "exclude alerts". Clicking the isolated chip again resets
    // the feed to all categories.
    setHistCategories(prev =>
      prev.length === 1 && prev[0] === cat ? ALL_CATEGORIES : [cat]
    )
  }

  // Load customers + fleets + balise assignment history when the Assignment tab opens
  const loadAssignmentData = async () => {
    try {
      const [custRes, fleetsRes, membershipsRes, historyRes, customerHistoryRes] = await Promise.all([
        apiClient.get('/customers'),
        apiClient.get('/fleets'),
        apiClient.get('/fleets/memberships'),
        apiClient.get(`/vehicles/${vehicle.vehicle_source}/${vehicle.id}/assignment-history`),
        apiClient.get(`/vehicles/${vehicle.vehicle_source}/${vehicle.id}/customer-history`)
      ])
      setCustomers(custRes.data || [])
      setFleets(fleetsRes.data || [])
      const mine = (membershipsRes.data || []).filter((m: any) =>
        m.vehicle_source === vehicle.vehicle_source && Number(m.vehicle_id) === Number(vehicle.id))
      setVehicleFleets(mine)
      setAssignmentHistory(historyRes.data || [])
      setCustomerHistory(customerHistoryRes.data || [])
    } catch (error) {
      console.error('Error loading assignment data:', error)
    }
  }
  useEffect(() => {
    if (isOpen && activeTab === 'assignment') loadAssignmentData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeTab, vehicle.id])

  // Who drives this vehicle, and who did before.
  const loadDriverData = useCallback(async () => {
    setDriverLoading(true)
    try {
      const [historyRes, driversRes] = await Promise.all([
        apiClient.get(`/vehicles/${vehicle.vehicle_source}/${vehicle.id}/driver-history`),
        apiClient.get('/drivers')
      ])
      setDriverAssignments(historyRes.data?.assignments || [])
      setDriverOptions(driversRes.data?.drivers || [])
    } catch (error) {
      console.error('Error loading driver assignments:', error)
    } finally {
      setDriverLoading(false)
    }
  }, [vehicle.vehicle_source, vehicle.id])

  useEffect(() => {
    if (isOpen && activeTab === 'driver') loadDriverData()
  }, [isOpen, activeTab, loadDriverData])

  const currentDriverAssignment = driverAssignments.find(item => item.isCurrent) || null

  const assignDriver = async () => {
    if (!selectedDriverId) return
    setDriverBusy(true)
    try {
      await apiClient.post('/driver-assignments', {
        staff_id: Number(selectedDriverId),
        vehicle_source: vehicle.vehicle_source,
        vehicle_id: vehicle.id
      })
      setSelectedDriverId('')
      showMessage?.(t('driver_assigned', { defaultValue: 'Driver assigned' }), 'success')
      await loadDriverData()
    } catch (error: any) {
      showMessage?.(error?.message || t('driver_assign_failed', { defaultValue: 'Failed to assign the driver' }), 'error')
    } finally {
      setDriverBusy(false)
    }
  }

  const endDriverAssignment = async (assignmentId: number) => {
    setDriverBusy(true)
    try {
      await apiClient.put(`/driver-assignments/${assignmentId}/end`, {})
      showMessage?.(t('driver_released', { defaultValue: 'Driver released' }), 'success')
      await loadDriverData()
    } catch (error: any) {
      showMessage?.(error?.message || t('driver_release_failed', { defaultValue: 'Failed to release the driver' }), 'error')
    } finally {
      setDriverBusy(false)
    }
  }

  const handleFieldChange = (field: keyof FleetVehicle, value: any) => {
    setEditData(prev => ({ ...prev, [field]: value }))
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      const currentCustomerId = vehicle.customer_id == null ? '' : String(vehicle.customer_id)
      if (selectedCustomerId !== currentCustomerId && !reassignmentReason.trim()) {
        showMessage?.('Enter a reason to change the customer owner', 'warning')
        return
      }
      await apiClient.put(`/vehicles/${vehicle.vehicle_source}/${vehicle.id}/attributes`, {
        brand: editData.make || undefined,
        model: editData.model || undefined,
        license_plate: editData.license_plate || undefined,
        asset_type: editData.asset_type && editData.asset_type !== vehicle.asset_type ? editData.asset_type : undefined,
        icon_key: editData.icon_key || undefined,
        equipment_reference: editData.equipment_reference || undefined,
        container_type: editData.container_type || undefined,
        container_owner: editData.container_owner || undefined,
        origin: (routeTouched ? route.departure : editData.container_origin) || undefined,
        destination: (routeTouched ? route.destination : editData.container_destination) || undefined,
        booking_reference: editData.booking_reference || undefined,
        custom_name: editData.custom_name || undefined,
        current_location: editData.current_location || undefined,
        year_manufactured: editData.year || undefined,
        length: editData.length || undefined,
        width: editData.width || undefined,
        height: editData.height || undefined,
        weight: editData.weight || undefined,
        max_weight: editData.max_weight || undefined,
        engine_type: editData.engine_type || undefined,
        consumption: editData.consumption || undefined,
        max_speed: editData.max_speed || undefined
      })
      if (selectedCustomerId !== currentCustomerId) {
        await apiClient.put(`/vehicles/${vehicle.vehicle_source}/${vehicle.id}/customer`, {
          customer_id: selectedCustomerId ? Number(selectedCustomerId) : null,
          reason: reassignmentReason.trim()
        })
      }
      // Update the transport assignment (itinerary or places) if the user changed it
      if (routeTouched && routeSelectionComplete(route)) {
        await applyTransportSelection(vehicle.vehicle_source as 'ASSET' | 'CONTAINER', vehicle.id, route)
      }
      setRouteTouched(false)
      setIsEditing(false)
      showMessage?.(t('vehicle_updated', { defaultValue: 'Vehicle updated successfully' }), 'success')
      onSuccess?.()
    } catch (error) {
      console.error('Error updating vehicle:', error)
      showMessage?.(t('vehicle_update_failed', { defaultValue: 'Error updating vehicle' }), 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleLinkCustomer = async () => {
    if (!selectedCustomerId || !reassignmentReason.trim()) return
    const cust = customers.find(c => c.id === Number(selectedCustomerId))
    const impact = `${vehicle.balise_count || 0} balise(s) and ${vehicleFleets.length} fleet membership(s) may be affected.`
    if (!window.confirm(`Reassign ${vehicle.vehicle_code} to ${cust?.company_name}?\n\n${impact}`)) return
    setCustomerBusy(true)
    try {
      await apiClient.put(`/vehicles/${vehicle.vehicle_source}/${vehicle.id}/customer`, {
        customer_id: Number(selectedCustomerId),
        reason: reassignmentReason.trim()
      })
      showMessage?.(t('customer_linked', { defaultValue: 'Customer {{name}} linked to {{vehicle}}', name: cust?.company_name, vehicle: vehicle.vehicle_code }), 'success')
      setReassignmentReason('')
      onSuccess?.()
    } catch (error) {
      console.error('Link customer error:', error)
      showMessage?.(t('customer_link_failed', { defaultValue: 'Failed to link customer' }), 'error')
    } finally {
      setCustomerBusy(false)
    }
  }

  const handleUnlinkCustomer = async () => {
    if (!reassignmentReason.trim()) return
    if (!window.confirm(`Remove the customer from ${vehicle.vehicle_code}? Existing balise and fleet links are not removed automatically.`)) return
    setCustomerBusy(true)
    try {
      await apiClient.put(`/vehicles/${vehicle.vehicle_source}/${vehicle.id}/customer`, {
        customer_id: null,
        reason: reassignmentReason.trim()
      })
      showMessage?.(t('customer_unlinked', { defaultValue: 'Customer unlinked from {{vehicle}}', vehicle: vehicle.vehicle_code }), 'success')
      setReassignmentReason('')
      onSuccess?.()
    } catch (error) {
      console.error('Unlink customer error:', error)
      showMessage?.(t('customer_unlink_failed', { defaultValue: 'Failed to unlink customer' }), 'error')
    } finally {
      setCustomerBusy(false)
    }
  }

  const handleAddToFleet = async () => {
    if (!selectedFleetId) return
    setFleetBusy(true)
    try {
      await apiClient.post(`/fleets/${selectedFleetId}/members`, {
        vehicle_source: vehicle.vehicle_source,
        vehicle_id: vehicle.id
      })
      showMessage?.(t('added_to_fleet', { defaultValue: '{{vehicle}} added to fleet', vehicle: vehicle.vehicle_code }), 'success')
      setSelectedFleetId('')
      await loadAssignmentData()
    } catch (error: any) {
      const msg = error?.response?.data?.error || t('add_to_fleet_failed', { defaultValue: 'Failed to add vehicle to fleet' })
      showMessage?.(msg, 'error')
    } finally {
      setFleetBusy(false)
    }
  }

  const handleRemoveFromFleet = async (fleetId: number, fleetName: string) => {
    setFleetBusy(true)
    try {
      await apiClient.delete(`/fleets/${fleetId}/members/${vehicle.vehicle_source}/${vehicle.id}`)
      showMessage?.(t('removed_from_fleet', { defaultValue: '{{vehicle}} removed from {{fleet}}', vehicle: vehicle.vehicle_code, fleet: fleetName }), 'success')
      await loadAssignmentData()
    } catch (error) {
      console.error('Remove from fleet error:', error)
      showMessage?.(t('remove_from_fleet_failed', { defaultValue: 'Failed to remove vehicle from fleet' }), 'error')
    } finally {
      setFleetBusy(false)
    }
  }

  if (!isOpen) return null

  const tabs: { id: VehicleModalTab, label: string, icon: React.ReactNode }[] = [
    { id: 'history', label: t('activity', { defaultValue: 'Activity' }), icon: <History className="w-4 h-4" /> },
    { id: 'details', label: t('details', { defaultValue: 'Details' }), icon: <Pencil className="w-4 h-4" /> },
    { id: 'assignment', label: t('vcc_tracking_assignment', { defaultValue: 'Tracking & Assignment' }), icon: <Building2 className="w-4 h-4" /> },
    { id: 'driver', label: t('driver', { defaultValue: 'Driver' }), icon: <Contact className="w-4 h-4" /> },
    { id: 'zones', label: t('vcc_zones_routes', { defaultValue: 'Zones & Routes' }), icon: <Route className="w-4 h-4" /> },
    { id: 'rules', label: t('vcc_alert_rules', { defaultValue: 'Alert Rules' }), icon: <ShieldCheck className="w-4 h-4" /> },
    { id: 'alerts', label: t('alerts', { defaultValue: 'Alerts' }), icon: <BellRing className="w-4 h-4" /> }
  ]
  const HeaderIcon = vehicleIconComponent(vehicle.icon_key, vehicle.asset_type || vehicle.vehicle_type, vehicle.vehicle_source === 'CONTAINER')
  const trackingMeta = trackingPresentation(vehicle.tracking_status, vehicle.current_speed)

  const categoryMeta: Record<ActivityCategory, { label: string, icon: React.ReactNode, chip: string, active: string }> = {
    TRACKING: { label: t('cat_tracking', { defaultValue: 'Tracking' }), icon: <Navigation className="w-3.5 h-3.5" />, chip: 'bg-blue-50 text-blue-700', active: 'bg-blue-600 text-white border-blue-600' },
    ALERT: { label: t('cat_alerts', { defaultValue: 'Alerts' }), icon: <AlertTriangle className="w-3.5 h-3.5" />, chip: 'bg-red-50 text-red-700', active: 'bg-red-600 text-white border-red-600' },
    GEOFENCE: { label: t('cat_geofence', { defaultValue: 'Geofence' }), icon: <Hexagon className="w-3.5 h-3.5" />, chip: 'bg-purple-50 text-purple-700', active: 'bg-purple-600 text-white border-purple-600' },
    ASSIGNMENT: { label: t('cat_assignments', { defaultValue: 'Assignments' }), icon: <Radio className="w-3.5 h-3.5" />, chip: 'bg-teal-50 text-teal-700', active: 'bg-teal-600 text-white border-teal-600' },
    MISSION: { label: t('cat_missions', { defaultValue: 'Missions' }), icon: <Route className="w-3.5 h-3.5" />, chip: 'bg-amber-50 text-amber-700', active: 'bg-amber-600 text-white border-amber-600' }
  }

  return (
    <div className={embedded ? 'h-full min-h-0' : 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[300] p-4'}>
      <div className={embedded
        ? 'bg-slate-50 w-full h-full min-w-[320px] overflow-hidden flex flex-col'
        : 'bg-slate-50 rounded-lg shadow-xl w-full min-w-[320px] max-w-[95vw] sm:max-w-2xl md:max-w-4xl lg:max-w-5xl max-h-[85vh] overflow-hidden flex flex-col'}>
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4 px-5 py-4 border-b border-gray-200 bg-white flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-slate-100 rounded-lg">
              <HeaderIcon className={`w-6 h-6 ${trackingMeta.icon}`} />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h2 className="text-xl font-semibold text-gray-900">
                  {vehicle.custom_name || vehicle.vehicle_code}
                </h2>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  trackingMeta.badge
                }`}>
                  {t(trackingMeta.labelKey, { defaultValue: trackingMeta.label })}
                </span>
              </div>
              <p className="text-sm text-gray-600">
                <strong>{vehicle.operational_identifier || vehicle.license_plate || vehicle.equipment_reference || vehicle.vehicle_code}</strong>
                {' • '}
                {vehicle.vehicle_source === 'ASSET'
                  ? [vehicle.make, vehicle.model].filter(Boolean).join(' ')
                  : [vehicle.container_owner, vehicle.container_type].filter(Boolean).join(' · ')}
                {' • '}{vehicle.customer_name || t('no_customer', { defaultValue: 'No customer' })}
                {vehicle.balise_count > 0 && vehicle.balise_last_seen && (
                  <span className="text-gray-400"> • {t('last_seen', { defaultValue: 'Last seen' })} {new Date(vehicle.balise_last_seen).toLocaleString()}</span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {onShowOnMap && (
              <button
                onClick={() => onShowOnMap(vehicle)}
                className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
              >
                <Map className="w-4 h-4" />
                <span>{t('show_on_map', { defaultValue: 'Show on map' })}</span>
              </button>
            )}
            {!embedded && <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>}
          </div>
        </div>

        <div className="border-b border-gray-200 bg-slate-50 px-5 py-3 flex-shrink-0">
          <ControlCenterSummary summary={controlCenter?.summary} loading={controlLoading} onSelect={target => onTabChange(target)} />
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 flex-shrink-0">
          <div className="flex space-x-6 px-6 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                className={`py-3 px-2 border-b-2 font-medium text-sm flex items-center space-x-2 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto p-4 md:p-6">
          {/* ================= ACTIVITY (unified filterable timeline) ================= */}
          {activeTab === 'history' && (
            <div className="space-y-4">
              {/* Category chips */}
              <div className="flex flex-wrap gap-2">
                {ALL_CATEGORIES.map(cat => {
                  const meta = categoryMeta[cat]
                  const selected = histCategories.includes(cat)
                  const count = histCounts[cat] ?? 0
                  return (
                    <button
                      key={cat}
                      onClick={() => toggleCategory(cat)}
                      className={`inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        selected ? meta.active : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {meta.icon}
                      <span>{meta.label}</span>
                      <span className={`px-1.5 rounded-full ${selected ? 'bg-white/25' : 'bg-gray-100'}`}>{count}</span>
                    </button>
                  )
                })}
              </div>

              {/* Filters */}
              <div className="bg-gray-50 rounded-lg p-4 flex flex-wrap items-end gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('from', { defaultValue: 'From' })}</label>
                  <input type="date" value={histFrom} onChange={e => setHistFrom(e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('to', { defaultValue: 'To' })}</label>
                  <input type="date" value={histTo} onChange={e => setHistTo(e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('event_type', { defaultValue: 'Event type' })}</label>
                  <select value={histType} onChange={e => setHistType(e.target.value)} className={inputCls}>
                    <option value="all">{t('all_types', { defaultValue: 'All types' })}</option>
                    {histTypes.map(type => <option key={type} value={type}>{type}</option>)}
                  </select>
                </div>
                {(histFrom || histTo || histType !== 'all' || histCategories.length < ALL_CATEGORIES.length) && (
                  <button
                    onClick={() => { setHistFrom(''); setHistTo(''); setHistType('all'); setHistCategories(ALL_CATEGORIES) }}
                    className="px-3 py-1.5 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-100"
                  >
                    {t('clear_filters', { defaultValue: 'Clear filters' })}
                  </button>
                )}
                <div className="ml-auto text-xs text-gray-500 flex items-center space-x-1">
                  <Filter className="w-3 h-3" />
                  <span>{t('events_shown', { defaultValue: '{{shown}} of {{total}} events', shown: histItems.length, total: histTotal })}</span>
                </div>
              </div>

              {histLoading ? (
                <div className="flex items-center justify-center h-40">
                  <Loader className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : histItems.length === 0 ? (
                <div className="py-12 text-center">
                  <History className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm font-medium text-gray-700">{t('no_activity', { defaultValue: 'No activity for this vehicle in the selected period' })}</p>
                  <p className="text-xs text-gray-500 mt-1">{t('no_activity_hint', { defaultValue: 'Activity appears here while a balise is assigned and reporting.' })}</p>
                </div>
              ) : (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="max-h-[45vh] overflow-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0 z-10">
                        <tr className="text-left text-xs text-gray-500 uppercase tracking-wider">
                          <th className="px-4 py-2">{t('time', { defaultValue: 'Time' })}</th>
                          <th className="px-4 py-2">{t('category', { defaultValue: 'Category' })}</th>
                          <th className="px-4 py-2">{t('event', { defaultValue: 'Event' })}</th>
                          <th className="px-4 py-2">{t('balise', { defaultValue: 'Balise' })}</th>
                          <th className="px-4 py-2">{t('position', { defaultValue: 'Position' })}</th>
                          <th className="px-4 py-2">{t('speed', { defaultValue: 'Speed' })}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {histItems.map(item => {
                          const meta = categoryMeta[item.category] || categoryMeta.TRACKING
                          return (
                            <tr key={item.id} className="hover:bg-gray-50">
                              <td className="px-4 py-2 whitespace-nowrap text-gray-900">{new Date(item.timestamp).toLocaleString()}</td>
                              <td className="px-4 py-2 whitespace-nowrap">
                                <span className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-medium ${meta.chip}`}>
                                  {meta.icon}
                                  <span>{meta.label}</span>
                                </span>
                              </td>
                              <td className="px-4 py-2">
                                <span className="font-medium text-gray-900">{item.title}</span>
                                {item.subtitle && <span className="block text-xs text-gray-500">{item.subtitle}</span>}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap text-gray-600">{item.lock_code || '—'}</td>
                              <td className="px-4 py-2 whitespace-nowrap text-gray-600">
                                {item.latitude != null && item.longitude != null && !(Number(item.latitude) === 0 && Number(item.longitude) === 0) ? (
                                  <span className="inline-flex items-center space-x-1">
                                    <MapPin className="w-3 h-3 text-gray-400" />
                                    <span>{Number(item.latitude).toFixed(4)}, {Number(item.longitude).toFixed(4)}</span>
                                  </span>
                                ) : '—'}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap text-gray-600">{item.speed != null ? `${Number(item.speed).toFixed(0)} km/h` : '—'}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ================= DETAILS (view + real edit) ================= */}
          {activeTab === 'details' && (
            <div className="space-y-6">
              <div className="flex justify-end">
                {!isEditing ? (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                  >
                    <Pencil className="w-4 h-4" />
                    <span>{t('edit_vehicle', { defaultValue: 'Edit vehicle' })}</span>
                  </button>
                ) : (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setIsEditing(false)}
                      disabled={saving}
                      className="px-3 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      {t('cancel')}
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center space-x-2"
                    >
                      {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      <span>{saving ? t('saving', { defaultValue: 'Saving…' }) : t('save_changes', { defaultValue: 'Save changes' })}</span>
                    </button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Identity */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-3">{t('basic_information', { defaultValue: 'Basic Information' })}</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('vehicle')} Code:</span>
                      <span className="font-medium">{vehicle.vehicle_code}</span>
                    </div>
                    {isEditing ? (
                      <>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Customer owner *</label>
                          <select value={selectedCustomerId} onChange={e => setSelectedCustomerId(e.target.value)} className={inputCls}>
                            <option value="">No customer</option>
                            {customers.map(customer => (
                              <option key={customer.id} value={customer.id}>{customer.company_name} ({customer.customer_code})</option>
                            ))}
                          </select>
                        </div>
                        {selectedCustomerId !== (vehicle.customer_id == null ? '' : String(vehicle.customer_id)) && (
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Customer change reason *</label>
                            <input type="text" value={reassignmentReason} onChange={e => setReassignmentReason(e.target.value)}
                              placeholder="Required for audit history" className={inputCls} />
                          </div>
                        )}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">{t('custom_name', { defaultValue: 'Custom Name' })}</label>
                          <input type="text" value={editData.custom_name || ''} onChange={e => handleFieldChange('custom_name', e.target.value)} className={inputCls} />
                        </div>
                        {vehicle.vehicle_source === 'ASSET' ? (
                          <>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Asset type *</label>
                              <select value={editData.asset_type || ''} onChange={e => handleFieldChange('asset_type', e.target.value)} className={inputCls}>
                                {editData.asset_type && !ASSET_TYPE_OPTIONS.some(option => option.value === editData.asset_type) && (
                                  <option value={editData.asset_type}>Legacy: {editData.asset_type} — select a precise type</option>
                                )}
                                {ASSET_TYPE_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">{t('license_plate', { defaultValue: 'License Plate / registration' })}</label>
                              <input type="text" value={editData.license_plate || ''} onChange={e => handleFieldChange('license_plate', e.target.value.toUpperCase())} className={inputCls} />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">{t('make', { defaultValue: 'Brand' })}</label>
                                <input type="text" value={editData.make || ''} onChange={e => handleFieldChange('make', e.target.value)} className={inputCls} />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">{t('model', { defaultValue: 'Model' })}</label>
                                <input type="text" value={editData.model || ''} onChange={e => handleFieldChange('model', e.target.value)} className={inputCls} />
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Fleet and map icon</label>
                              <div className="grid grid-cols-5 gap-1.5">
                                {VEHICLE_ICON_OPTIONS.filter(option => option.key !== 'container').map(option => (
                                  <button key={option.key} type="button" title={option.label} onClick={() => handleFieldChange('icon_key', option.key)}
                                    className={`p-2 rounded-lg border flex justify-center ${editData.icon_key === option.key ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-500'}`}>
                                    <option.Icon size={18} />
                                  </button>
                                ))}
                              </div>
                            </div>
                            <div className="rounded-lg border border-gray-200 bg-white p-3">
                              <div className="text-xs font-semibold text-gray-700 mb-1.5">Transport route (optional)</div>
                              <TransportRoutePicker
                                value={route}
                                onChange={next => { setRoute(next); setRouteTouched(true) }}
                              />
                            </div>
                          </>
                        ) : (
                          <>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Equipment / container number *</label>
                              <input type="text" value={editData.equipment_reference || ''} onChange={e => handleFieldChange('equipment_reference', e.target.value.toUpperCase())} className={inputCls} />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">ISO container type *</label>
                              <select value={editData.container_type || ''} onChange={e => handleFieldChange('container_type', e.target.value)} className={inputCls}>
                                {['20GP', '40GP', '40HC', '20RF', '40RF', '45HC', 'FLAT', 'TANK'].map(type => <option key={type}>{type}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Shipping-line owner *</label>
                              <input type="text" value={editData.container_owner || ''} onChange={e => handleFieldChange('container_owner', e.target.value)} className={inputCls} />
                            </div>
                            <div className="rounded-lg border border-gray-200 bg-white p-3">
                              <div className="text-xs font-semibold text-gray-700 mb-1.5">Transport route</div>
                              {!routeTouched && (vehicle.container_origin || vehicle.container_destination) && (
                                <p className="text-xs text-gray-500 mb-2">
                                  Current: {[vehicle.container_origin, vehicle.container_destination].filter(Boolean).join(' → ')} — pick below to replace it.
                                </p>
                              )}
                              <TransportRoutePicker
                                value={route}
                                onChange={next => { setRoute(next); setRouteTouched(true) }}
                              />
                            </div>
                          <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Booking reference</label>
                              <input type="text" value={editData.booking_reference || ''} onChange={e => handleFieldChange('booking_reference', e.target.value)} className={inputCls} />
                          </div>
                          </>
                        )}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">{t('year', { defaultValue: 'Year' })}</label>
                          <input type="number" min={1900} max={2035} value={editData.year || ''} onChange={e => handleFieldChange('year', parseInt(e.target.value))} className={inputCls} />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">{t('base_location', { defaultValue: 'Base location (manual note)' })}</label>
                          <input type="text" value={editData.current_location || ''} onChange={e => handleFieldChange('current_location', e.target.value)} className={inputCls} />
                          <p className="mt-1 text-[11px] text-gray-400">{t('base_location_hint', { defaultValue: 'Free note (depot, yard…). Live GPS position comes from the balise; departure is the transport start point.' })}</p>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Customer owner:</span>
                          <span className="font-medium">{vehicle.customer_name || '—'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">{t('custom_name', { defaultValue: 'Custom Name' })}:</span>
                          <span className="font-medium">{vehicle.custom_name || '—'}</span>
                        </div>
                        {vehicle.vehicle_source === 'ASSET' ? (
                          <>
                            <div className="flex justify-between"><span className="text-gray-600">Asset type:</span><span className="font-medium">{vehicle.asset_type || vehicle.vehicle_type}</span></div>
                            <div className="flex justify-between"><span className="text-gray-600">License plate:</span><span className="font-medium">{vehicle.license_plate || '—'}</span></div>
                            <div className="flex justify-between"><span className="text-gray-600">Brand / model:</span><span className="font-medium">{[vehicle.make, vehicle.model].filter(Boolean).join(' ') || '—'}</span></div>
                          </>
                        ) : (
                          <>
                            <div className="flex justify-between"><span className="text-gray-600">Container number:</span><span className="font-medium">{vehicle.equipment_reference || vehicle.vehicle_code}</span></div>
                            <div className="flex justify-between"><span className="text-gray-600">ISO type:</span><span className="font-medium">{vehicle.container_type || '—'}</span></div>
                            <div className="flex justify-between"><span className="text-gray-600">Shipping owner:</span><span className="font-medium">{vehicle.container_owner || '—'}</span></div>
                            <div className="flex justify-between"><span className="text-gray-600">Route:</span><span className="font-medium">{[vehicle.container_origin, vehicle.container_destination].filter(Boolean).join(' → ') || '—'}</span></div>
                            <div className="flex justify-between"><span className="text-gray-600">Booking:</span><span className="font-medium">{vehicle.booking_reference || '—'}</span></div>
                          </>
                        )}
                        <div className="flex justify-between">
                          <span className="text-gray-600">{t('year', { defaultValue: 'Year' })}:</span>
                          <span className="font-medium">{vehicle.year}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">{t('base_location', { defaultValue: 'Base location (manual note)' })}:</span>
                          <span className="font-medium">{vehicle.current_location || '—'}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Specifications */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-3">{t('specifications', { defaultValue: 'Specifications' })}</h3>
                  {isEditing ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">{t('length', { defaultValue: 'Length (m)' })}</label>
                          <input type="number" step="0.1" min="0" value={editData.length ?? ''} onChange={e => handleFieldChange('length', parseFloat(e.target.value) || undefined)} className={inputCls} />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">{t('width', { defaultValue: 'Width (m)' })}</label>
                          <input type="number" step="0.1" min="0" value={editData.width ?? ''} onChange={e => handleFieldChange('width', parseFloat(e.target.value) || undefined)} className={inputCls} />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">{t('height', { defaultValue: 'Height (m)' })}</label>
                          <input type="number" step="0.1" min="0" value={editData.height ?? ''} onChange={e => handleFieldChange('height', parseFloat(e.target.value) || undefined)} className={inputCls} />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">{t('max_weight', { defaultValue: 'Max Weight (kg)' })}</label>
                          <input type="number" min="0" value={editData.max_weight ?? ''} onChange={e => handleFieldChange('max_weight', parseFloat(e.target.value) || undefined)} className={inputCls} />
                        </div>
                        {vehicle.vehicle_source === 'ASSET' && <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">{t('engine_type', { defaultValue: 'Engine Type' })}</label>
                          <input type="text" value={editData.engine_type || ''} onChange={e => handleFieldChange('engine_type', e.target.value)} className={inputCls} />
                        </div>}
                      </div>
                      {vehicle.vehicle_source === 'ASSET' && <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">{t('consumption', { defaultValue: 'Consumption (L/100km)' })}</label>
                          <input type="number" step="0.1" min="0" value={editData.consumption ?? ''} onChange={e => handleFieldChange('consumption', parseFloat(e.target.value) || undefined)} className={inputCls} />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">{t('max_speed', { defaultValue: 'Max Speed (km/h)' })}</label>
                          <input type="number" min="0" value={editData.max_speed ?? ''} onChange={e => handleFieldChange('max_speed', parseFloat(e.target.value) || undefined)} className={inputCls} />
                        </div>
                      </div>}
                    </div>
                  ) : (
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('dimensions', { defaultValue: 'Dimensions' })}:</span>
                        <span className="font-medium">
                          {vehicle.length || vehicle.width || vehicle.height
                            ? `${vehicle.length || '?'}m × ${vehicle.width || '?'}m × ${vehicle.height || '?'}m`
                            : '—'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('max_weight', { defaultValue: 'Max Weight' })}:</span>
                        <span className="font-medium">{vehicle.max_weight ? `${vehicle.max_weight} kg` : '—'}</span>
                      </div>
                      {vehicle.vehicle_source === 'ASSET' && <div className="flex justify-between">
                        <span className="text-gray-600">{t('engine_type', { defaultValue: 'Engine Type' })}:</span>
                        <span className="font-medium">{vehicle.engine_type || '—'}</span>
                      </div>}
                      {vehicle.vehicle_source === 'ASSET' && <div className="flex justify-between">
                        <span className="text-gray-600">{t('consumption', { defaultValue: 'Consumption' })}:</span>
                        <span className="font-medium">{vehicle.consumption ? `${vehicle.consumption} L/100km` : '—'}</span>
                      </div>}
                      {vehicle.vehicle_source === 'ASSET' && <div className="flex justify-between">
                        <span className="text-gray-600">{t('max_speed', { defaultValue: 'Max Speed' })}:</span>
                        <span className="font-medium">{vehicle.max_speed ? `${vehicle.max_speed} km/h` : '—'}</span>
                      </div>}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ================= ASSIGNMENT (customer + fleets) ================= */}
          {activeTab === 'assignment' && (
            <div className="space-y-6">
              <div className="rounded-lg border border-blue-100 bg-blue-50 p-4">
                <h3 className="mb-3 flex items-center gap-2 font-medium text-gray-900">
                  <Radio className="h-4 w-4 text-blue-600" />
                  <span>{t('cat_tracking', { defaultValue: 'Tracking' })}</span>
                </h3>
                <div className="grid gap-3 text-sm sm:grid-cols-3">
                  <div className="rounded-lg bg-white p-3">
                    <p className="text-xs text-gray-500">{t('status')}</p>
                    <p className="mt-1 font-semibold text-gray-900">{String(controlCenter?.tracking?.status || controlCenter?.tracking?.tracking_status || vehicle.tracking_status || 'NOT_TRACKED')}</p>
                  </div>
                  <div className="rounded-lg bg-white p-3">
                    <p className="text-xs text-gray-500">{t('balise', { defaultValue: 'Tracker' })}</p>
                    <p className="mt-1 font-semibold text-gray-900">{controlCenter?.balises?.length ?? vehicle.balise_count ?? 0}</p>
                  </div>
                  <div className="rounded-lg bg-white p-3">
                    <p className="text-xs text-gray-500">{t('last_seen', { defaultValue: 'Last seen' })}</p>
                    <p className="mt-1 font-semibold text-gray-900">
                      {String(controlCenter?.tracking?.last_seen || vehicle.balise_last_seen || '—')}
                    </p>
                  </div>
                </div>
              </div>
              {Boolean(controlCenter?.missions.length) && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-3 flex items-center space-x-2">
                    <Route className="w-4 h-4 text-amber-600" />
                    <span>{t('vcc_active_missions', { defaultValue: 'Active missions' })}</span>
                  </h3>
                  <div className="grid gap-2 md:grid-cols-2">
                    {controlCenter!.missions.map(mission => (
                      <button
                        key={mission.id}
                        onClick={() => handleManageMission(mission)}
                        className="rounded-lg border border-gray-200 bg-white p-3 text-left hover:border-amber-300 hover:bg-amber-50"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-semibold text-gray-900">{mission.mission_name || mission.name || mission.id}</span>
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800">{mission.mission_status || 'ACTIVE'}</span>
                        </div>
                        <p className="mt-1 text-xs text-gray-500">{t('vcc_open_mission', { defaultValue: 'Open mission management' })}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {/* Customer */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-3 flex items-center space-x-2">
                  <Building2 className="w-4 h-4 text-blue-600" />
                  <span>{t('customer', { defaultValue: 'Customer' })}</span>
                </h3>
                <div className="mb-3 p-3 rounded-lg bg-white border border-gray-200">
                  <p className="text-xs text-gray-500">{t('current_customer', { defaultValue: 'Current customer' })}</p>
                  <p className="text-sm font-semibold text-gray-900">{vehicle.customer_name || t('no_customer', { defaultValue: 'No customer assigned' })}</p>
                  {vehicle.customer_code && <p className="text-xs text-gray-500">{vehicle.customer_code}</p>}
                </div>
                <div className="space-y-2">
                  <select
                    value={selectedCustomerId}
                    onChange={e => setSelectedCustomerId(e.target.value)}
                    className={inputCls}
                  >
                    <option value="">{t('select_customer', { defaultValue: 'Select the new customer…' })}</option>
                    {customers.map(c => (
                      <option key={c.id} value={c.id}>{c.company_name} ({c.customer_code})</option>
                    ))}
                  </select>
                  <input
                    value={reassignmentReason}
                    onChange={e => setReassignmentReason(e.target.value)}
                    placeholder={t('reassignment_reason', { defaultValue: 'Reason for assignment or transfer *' }) as string}
                    className={inputCls}
                  />
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleLinkCustomer}
                      disabled={!selectedCustomerId || !reassignmentReason.trim() || customerBusy}
                      className="px-3 py-1.5 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-1.5 disabled:opacity-50"
                    >
                      <Link2 className="w-4 h-4" />
                      <span>{vehicle.customer_name ? t('reassign', { defaultValue: 'Reassign' }) : t('link_customer', { defaultValue: 'Assign' })}</span>
                    </button>
                    {vehicle.customer_name && (
                      <button
                        onClick={handleUnlinkCustomer}
                        disabled={!reassignmentReason.trim() || customerBusy}
                        className="px-3 py-1.5 text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors flex items-center space-x-1.5 disabled:opacity-50"
                      >
                        <Unlink className="w-4 h-4" />
                        <span>{t('unlink_customer', { defaultValue: 'Unlink' })}</span>
                      </button>
                    )}
                  </div>
                  <p className="text-[11px] text-gray-500">
                    {t('reassignment_warning', { defaultValue: 'The permanent vehicle code is preserved. Balise and fleet links are reviewed before transfer.' })}
                  </p>
                </div>
                {customerHistory.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-gray-200 space-y-2">
                    <p className="text-xs font-semibold text-gray-700">{t('customer_history', { defaultValue: 'Customer history' })}</p>
                    {customerHistory.slice(0, 5).map(entry => (
                      <div key={entry.id} className="text-xs text-gray-600 flex justify-between gap-3">
                        <span className="truncate">{entry.previous_customer_name || 'Unassigned'} → {entry.new_customer_name || 'Unassigned'} · {entry.reason}</span>
                        <span className="whitespace-nowrap text-gray-400">{new Date(entry.changed_at).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Fleets */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-3 flex items-center space-x-2">
                  <Boxes className="w-4 h-4 text-blue-600" />
                  <span>{t('fleets', { defaultValue: 'Fleets' })}</span>
                </h3>

                {vehicleFleets.length > 0 ? (
                  <div className="space-y-2 mb-4">
                    {vehicleFleets.map(f => (
                      <div key={f.fleet_id} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-3 py-2">
                        <span className="text-sm font-medium text-gray-900">{f.fleet_name}</span>
                        <button
                          onClick={() => handleRemoveFromFleet(f.fleet_id, f.fleet_name)}
                          disabled={fleetBusy}
                          className="text-orange-600 hover:text-orange-800 p-1 rounded hover:bg-orange-50 transition-colors disabled:opacity-50"
                          title={t('remove_from_fleet_btn', { defaultValue: 'Remove from fleet' }) as string}
                        >
                          <Unlink className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 mb-4">{t('vehicle_in_no_fleet', { defaultValue: 'This vehicle is not part of any fleet.' })}</p>
                )}

                {fleets.filter(f => !vehicleFleets.some(vf => vf.fleet_id === f.id)).length > 0 ? (
                  <div className="flex items-center space-x-2">
                    <select
                      value={selectedFleetId}
                      onChange={e => setSelectedFleetId(e.target.value)}
                      className={`${inputCls} flex-1`}
                    >
                      <option value="">{t('select_fleet', { defaultValue: 'Add to a fleet…' })}</option>
                      {fleets.filter(f => !vehicleFleets.some(vf => vf.fleet_id === f.id)).map(f => (
                        <option key={f.id} value={f.id}>{f.name}{f.customer_name ? ` — ${f.customer_name}` : ''} ({f.member_count})</option>
                      ))}
                    </select>
                    <button
                      onClick={handleAddToFleet}
                      disabled={!selectedFleetId || fleetBusy}
                      className="px-3 py-1.5 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-1.5 disabled:opacity-50"
                    >
                      <Link2 className="w-4 h-4" />
                      <span>{t('add', { defaultValue: 'Add' })}</span>
                    </button>
                  </div>
                ) : fleets.length === 0 && (
                  <p className="text-xs text-gray-500">{t('no_fleets_hint', { defaultValue: 'No fleets exist yet. Create one in the Fleets tab of Vehicle Management.' })}</p>
                )}
              </div>

              {/* Balise assignment history */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-3 flex items-center space-x-2">
                  <Radio className="w-4 h-4 text-blue-600" />
                  <span>{t('assignment_history', { defaultValue: 'Balise Assignment History' })}</span>
                </h3>
                {assignmentHistory.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    {t('no_assignment_history', { defaultValue: 'No balise has ever been assigned to this vehicle.' })}
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-200">
                          <th className="py-2 pr-4">{t('balise', { defaultValue: 'Balise' })}</th>
                          <th className="py-2 pr-4">{t('brand', { defaultValue: 'Brand' })}</th>
                          <th className="py-2 pr-4">{t('assigned_on', { defaultValue: 'Assigned on' })}</th>
                          <th className="py-2 pr-4">{t('unassigned_on', { defaultValue: 'Unassigned on' })}</th>
                          <th className="py-2 pr-4">{t('by', { defaultValue: 'By' })}</th>
                          <th className="py-2">{t('status')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {assignmentHistory.map((entry: any) => (
                          <tr key={entry.id}>
                            <td className="py-2 pr-4 font-medium text-gray-900">{entry.lock_code}</td>
                            <td className="py-2 pr-4 text-gray-600">{entry.brand || '—'}</td>
                            <td className="py-2 pr-4 text-gray-600">{entry.assignment_date ? new Date(entry.assignment_date).toLocaleString() : '—'}</td>
                            <td className="py-2 pr-4 text-gray-600">{entry.unassignment_date ? new Date(entry.unassignment_date).toLocaleString() : '—'}</td>
                            <td className="py-2 pr-4 text-gray-600">{entry.assigned_by || '—'}</td>
                            <td className="py-2">
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${entry.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                                {entry.is_active ? t('active', { defaultValue: 'Active' }) : t('ended', { defaultValue: 'Ended' })}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
          {/* ================= DRIVER ================= */}
          {activeTab === 'driver' && (
            <div className="space-y-4">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h3 className="font-medium text-gray-900 mb-3 flex items-center space-x-2">
                  <UserCheck className="w-4 h-4 text-blue-600" />
                  <span>{t('current_driver', { defaultValue: 'Current driver' })}</span>
                </h3>
                {driverLoading ? (
                  <p className="flex items-center gap-2 text-sm text-gray-500">
                    <Loader className="w-4 h-4 animate-spin" />{t('loading')}
                  </p>
                ) : currentDriverAssignment ? (
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-gray-900">{currentDriverAssignment.driverName}</p>
                      <p className="text-xs text-gray-500">
                        {currentDriverAssignment.driverCode ? `${currentDriverAssignment.driverCode} · ` : ''}
                        {t('since', { defaultValue: 'Since' })} {new Date(currentDriverAssignment.assignedFrom).toLocaleString()}
                      </p>
                    </div>
                    <button
                      onClick={() => endDriverAssignment(currentDriverAssignment.id)}
                      disabled={driverBusy}
                      className="flex items-center gap-2 rounded-lg border border-red-300 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                    >
                      <UserMinus className="w-4 h-4" />
                      {t('release_driver', { defaultValue: 'Release driver' })}
                    </button>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">
                    {t('no_current_driver', { defaultValue: 'No driver is assigned to this vehicle.' })}
                  </p>
                )}
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h3 className="font-medium text-gray-900 mb-3 flex items-center space-x-2">
                  <Contact className="w-4 h-4 text-blue-600" />
                  <span>{t('assign_driver', { defaultValue: 'Assign a driver' })}</span>
                </h3>
                {driverOptions.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    {t('no_drivers_hint', { defaultValue: 'No active driver exists yet. Add one in Fleet Staff Management.' })}
                  </p>
                ) : (
                  <div className="flex flex-wrap items-end gap-3">
                    <div className="min-w-[240px] flex-1">
                      <SearchableSelect
                        options={driverOptions.map(driver => ({
                          value: String(driver.id),
                          label: driver.name,
                          // Saying where a driver already is avoids a surprise handover.
                          hint: [driver.staffCode, driver.currentAssignment?.vehicleLabel].filter(Boolean).join(' · ') || null
                        }))}
                        value={selectedDriverId}
                        onChange={setSelectedDriverId}
                        placeholder={t('choose_driver', { defaultValue: 'Choose a driver' })}
                        searchPlaceholder={t('search_driver', { defaultValue: 'Search a driver…' })}
                        emptyText={t('no_match', { defaultValue: 'No match' })}
                      />
                    </div>
                    <button
                      onClick={assignDriver}
                      disabled={!selectedDriverId || driverBusy}
                      className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
                    >
                      {driverBusy ? <Loader className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                      {t('assign', { defaultValue: 'Assign' })}
                    </button>
                  </div>
                )}
                {currentDriverAssignment && (
                  <p className="mt-2 text-xs text-amber-700">
                    {t('driver_handover_hint', {
                      defaultValue: 'Assigning another driver ends the current assignment at the same moment.'
                    })}
                  </p>
                )}
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-3 flex items-center space-x-2">
                  <History className="w-4 h-4 text-blue-600" />
                  <span>{t('driver_history', { defaultValue: 'Driver history' })}</span>
                </h3>
                {driverAssignments.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    {t('no_driver_history', { defaultValue: 'No driver has ever been assigned to this vehicle.' })}
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-200">
                          <th className="py-2 pr-4">{t('driver', { defaultValue: 'Driver' })}</th>
                          <th className="py-2 pr-4">{t('from', { defaultValue: 'From' })}</th>
                          <th className="py-2 pr-4">{t('to', { defaultValue: 'To' })}</th>
                          <th className="py-2 pr-4">{t('by', { defaultValue: 'By' })}</th>
                          <th className="py-2">{t('status')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {driverAssignments.map(entry => (
                          <tr key={entry.id}>
                            <td className="py-2 pr-4 font-medium text-gray-900">
                              {entry.driverName}
                              {entry.driverCode && <span className="ml-1 text-xs text-gray-400">{entry.driverCode}</span>}
                            </td>
                            <td className="py-2 pr-4 text-gray-600">{new Date(entry.assignedFrom).toLocaleString()}</td>
                            <td className="py-2 pr-4 text-gray-600">
                              {entry.assignedTo ? new Date(entry.assignedTo).toLocaleString() : '—'}
                            </td>
                            <td className="py-2 pr-4 text-gray-600">{entry.createdBy || '—'}</td>
                            <td className="py-2">
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                                entry.isCurrent ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                              }`}>
                                {entry.isCurrent ? t('active', { defaultValue: 'Active' }) : t('ended', { defaultValue: 'Ended' })}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'zones' && (
            <VehicleZonesTab
              vehicleSource={vehicle.vehicle_source}
              vehicleId={vehicle.id}
              geofences={controlCenter?.geofences || []}
              corridors={controlCenter?.corridors || []}
              loading={controlLoading}
              error={controlError}
              onRefresh={loadControlCenter}
              onCreateZone={handleCreateZone}
              onManageZone={handleManageZone}
              showMessage={showMessage}
            />
          )}
          {activeTab === 'rules' && (
            <VehicleRulesTab
              rules={controlCenter?.alert_rules || []}
              loading={controlLoading}
              error={controlError}
              onRefresh={loadControlCenter}
              onCreateRule={handleCreateRule}
              onEditRule={handleEditRule}
              showMessage={showMessage}
            />
          )}
          {activeTab === 'alerts' && (
            <VehicleAlertsTab
              alerts={controlCenter?.alerts || []}
              loading={controlLoading}
              error={controlError}
              onRefresh={loadControlCenter}
              showMessage={showMessage}
            />
          )}
        </div>

        {/* Footer */}
        {!embedded && <div className="border-t border-gray-200 p-4 flex-shrink-0 flex items-center justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t('close')}
          </button>
        </div>}
      </div>
    </div>
  )
}

export default VehicleDetailsModal
